import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  RefreshCcw,
  Tag,
  Percent,
  UserPlus,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
} from "lucide-react";
import SpecialOfferModals from "./SpecialOfferModals";
import useSpecialOffer from "../../../services/Order/useSpecialOffer";
import LoadingScreen from "../../../utils/LoadingScreen";

export default function SpecialOffers() {
  const {
    offers,
    customerOffers,
    loading,
    fetchOffers,
    fetchCustomerOffers,
    deleteOffer,
    addOffer,
    assignOffer,
  } = useSpecialOffer();

  const [tab, setTab] = useState("offers");
  const [query, setQuery] = useState("");
  const [mobile, setMobile] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [assignMobile, setAssignMobile] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const [form, setForm] = useState({
    couponCode: "",
    offerTitle: "",
    offerDescription: "",
    discount: "",
    discountType: "fixed",
    minimumAmount: "",
    maximumDiscount: "",
    singleUse: true,
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return offers;

    return offers.filter((r) =>
      [
        r.coupon_code,
        r.offer_title,
        r.order_type,
        r.discount,
        r.min_amount,
        r.max_discount,
      ]
        .map((v) => String(v || "").toLowerCase())
        .some((s) => s.includes(needle)),
    );
  }, [offers, query]);

  const filteredCustomers = useMemo(() => {
    if (!mobile) return customerOffers;

    return customerOffers.filter((r) =>
      String(r.customer_mobile || "").includes(mobile),
    );
  }, [customerOffers, mobile]);

  return (
    <>
    
    <div className="p-6 min-h-0 h-full overflow-hidden flex flex-col montserrat-font-1">
      {/* HEADER */}
      <div className="bg-white p-5 rounded-lg mb-6 montserrat-font-1">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Special Offers & Coupons</h1>

          <div className="flex items-center gap-2 montserrat-font-1">
            {tab === "offers" && (
              <button
                onClick={() => setOpenModal(true)}
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm"
              >
                <Plus size={16} />
                New Offer
              </button>
            )}

            <button
              onClick={() =>
                tab === "offers" ? fetchOffers() : fetchCustomerOffers()
              }
              className="px-3 py-1.5 border rounded-lg text-white flex items-center gap-2 bg-green-500"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* TABS */}

        <div className="border-b mt-5 montserrat-font-1">
          <div className="flex gap-6 text-sm font-medium">
            <button
              onClick={() => setTab("offers")}
              className={`pb-2 flex items-center gap-2 border-b-2 ${
                tab === "offers"
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500"
              }`}
            >
              <Tag size={16} />
              Offer Master
            </button>

            <button
              onClick={() => {
                setTab("customer");
                fetchCustomerOffers();
              }}
              className={`pb-2 flex items-center gap-2 border-b-2 ${
                tab === "customer"
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500"
              }`}
            >
              <UserPlus size={16} />
              Customer Coupons
            </button>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}

      {tab === "offers" && (
        <div className="flex justify-between items-center mb-4 montserrat-font-1">
          <div className="flex items-center border rounded-lg px-3 py-2 w-[320px] bg-white montserrat-font-1">
            <Search size={16} className="text-gray-400 mr-2" />

            <input
              placeholder="Search by coupon code, offer title or description"
              className="outline-none text-sm w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="text-sm border px-3 py-1 rounded-full bg-white">
            Total: {filtered.length}
          </div>
        </div>
      )}

      {tab === "customer" && (
        <div className="flex justify-between items-center mb-4 montserrat-font-1">
          <div className="flex gap-2">
            <input
              placeholder="Enter 10-digit customer mobile (blank = all)…"
              className="border px-3 py-2 rounded-lg text-sm w-[280px]"
              value={mobile}
              onChange={(e) =>
                setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
            />

            <button
              onClick={() => fetchCustomerOffers(`91${mobile}`)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1"
            >
              <Search size={14} />
              Search
            </button>

            <button
              onClick={() => {
                setMobile("");
                fetchCustomerOffers();
              }}
              className="border px-4 py-2 rounded-lg text-sm flex items-center gap-1"
            >
              <X size={14} />
              Clear
            </button>
          </div>

          <div className="text-sm border px-3 py-1 rounded-full bg-white">
            Total: {customerOffers.length}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-6">
          <LoadingScreen text="Updating..." />
        </div>
      )}

      {/* TABLE */}

      <div className="bg-white rounded-xl border overflow-auto h-full montserrat-font-1">
        {!loading && tab === "offers" && (
          <table className="w-full text-sm montserrat-font-1">
            <thead className="sticky top-0 z-10 bg-green-100 text-gray-600 montserrat-font-1">
              <tr>
                <th className="p-3 text-left">Coupon</th>
                <th className="p-3 text-left">Offer</th>
                <th className="p-3 text-left">Discount</th>
                <th className="p-3 text-left">Min Amount</th>
                <th className="p-3 text-left">Max Discount</th>
                <th className="p-3 text-left">Single Use</th>
                <th className="p-3 text-left">Offer Status</th>
                <th className="p-3 text-left">Order Type</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => (
                <tr key={r.offerid} className="border-t">
                  <td className="p-3 flex items-center gap-2">
                    <Tag size={14} />
                    {r.coupon_code}
                  </td>

                  <td className="p-3">{r.offer_title}</td>

                  <td className="p-3">
                    <span className="px-3 py-1 rounded-full bg-green-100 text-xs flex items-center gap-1 w-fit">
                      <Percent size={12} />
                      {r.discount}
                    </span>
                  </td>

                  <td className="p-3">₹{r.min_amount}</td>

                  <td className="p-3">₹{r.max_discount}</td>

                  <td className="p-3">
                    {String(r.single_use).toLowerCase() === "y" ||
                    String(r.single_use).toLowerCase() === "yes" ? (
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        Yes
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                        No
                      </span>
                    )}
                  </td>

                  <td className="p-3">
                    {String(r.offer_status).toLowerCase() === "y" ||
                    String(r.offer_status).toLowerCase() === "active" ? (
                      <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </td>

                  <td className="p-3">
                    {r.order_type
                      ? r.order_type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      : "-"}
                  </td>

                  <td className="p-3 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setSelectedOffer(r);
                        setAssignOpen(true);
                      }}
                      className="p-2 border rounded-lg hover:bg-gray-50"
                    >
                      <UserPlus size={14} />
                    </button>

                    <button
                      onClick={() => {
                        setEditingOffer(r);

                        setForm({
                          couponCode: r.coupon_code,
                          offerTitle: r.offer_title,
                          offerDescription: r.offer_description,
                          discount: r.discount,
                          discountType: r.discount_type || "fixed",
                          minimumAmount: r.min_amount,
                          maximumDiscount: r.max_discount,
                          singleUse:
                            String(r.single_use).toLowerCase() === "y" ||
                            String(r.single_use).toLowerCase() === "yes",
                        });

                        setEditOpen(true);
                      }}
                      className="p-2 border rounded-lg hover:bg-gray-50 text-blue-600"
                    >
                      <Pencil size={14} />
                    </button>

                    <button
                      onClick={() => {
                        setDeleteId(r.offerid || r.id || r.pk_offer_id);
                        setDeleteOpen(true);
                      }}
                      className="p-2 border rounded-lg hover:bg-red-50 text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && tab === "customer" && (
          <table className="w-full text-sm montserrat-font-1">
            <thead className="sticky top-0 z-10 bg-green-100 text-gray-600 montserrat-font-1">
              <tr>
                <th className="p-3 text-left">Coupon</th>
                <th className="p-3 text-left">Customer Mobile</th>
                <th className="p-3 text-left">Discount</th>
                <th className="p-3 text-left">Min Amount</th>
                <th className="p-3 text-left">Max Discount</th>
                <th className="p-3 text-left">Discount Type</th>
                <th className="p-3 text-left">Coupon Title </th>
                <th className="p-3 text-left">Description</th>
                <th className="p-3 text-left">Single Use</th>
                <th className="p-3 text-left">Order Type</th>
                <th className="p-3 text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredCustomers.map((r) => (
                <tr key={r.pk_coupon_id} className="border-t">
                  <td className="p-3 flex items-center gap-2">
                    <Tag size={14} />
                    {r.coupon_code}
                  </td>

                  <td className="p-3">+91 {r.customer_mobile}</td>

                  <td className="p-3">
                    <span className="px-3 py-1 rounded-full bg-green-100 text-xs">
                      {r.discount}
                    </span>
                  </td>

                  <td className="p-3">₹{r.min_amount}</td>

                  <td className="p-3">₹{r.max_discount}</td>

                  <td className="p-3">{r.discount_type}</td>

                  <td className="p-3">{r.coupon_title}</td>

                  <td className="p-3 max-w-[350px]">{r.coupon_description}</td>
                  <td className="p-3">
                    {String(r.single_use).toLowerCase() === "y" ||
                    String(r.single_use).toLowerCase() === "yes" ? (
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        Yes
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                        No
                      </span>
                    )}
                  </td>

                  <td className="p-3">
                    {r.order_type
                      ? r.order_type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      : "-"}
                  </td>

                  <td className="p-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 w-fit ${
                        r.coupon_status === "Y"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      <CheckCircle2 size={12} />
                      {r.coupon_status === "Y" ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SpecialOfferModals
        openModal={openModal}
        setOpenModal={setOpenModal}
        assignOpen={assignOpen}
        setAssignOpen={setAssignOpen}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        form={form}
        setForm={setForm}
        addOffer={addOffer}
        assignOffer={assignOffer}
        deleteOffer={deleteOffer}
        editingOffer={editingOffer}
        selectedOffer={selectedOffer}
        assignMobile={assignMobile}
        setAssignMobile={setAssignMobile}
        deleteId={deleteId}
      />
    </div>
    </>
  );
}
